import { collection, doc, query, onSnapshot } from "firebase/firestore";
import { signInWithCustomToken, getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { db } from "./config";

const activeSubscriptions = new Map();

function unsubscribeAll() {
	for (const key of activeSubscriptions.keys()) {
		unsubscribeCollection(key);
	}
}

export async function signInEvents(token) {
	const auth = getAuth();
	const previousUid = auth.currentUser?.uid;

	const credential = await signInWithCustomToken(auth, token);

	// Only tear down active subscriptions when the signed-in identity actually
	// changed (e.g. user-group switch). Re-signing the same user — session
	// restore or events-token refresh — must keep live listeners intact:
	// Firestore re-authenticates open streams on its own, and unsubscribing
	// here would silently kill listeners created concurrently by bootstrapping
	// modules (their first snapshot never arrives and the app hangs).
	if (previousUid && previousUid !== credential.user.uid) {
		unsubscribeAll();
	}

	return credential;
}

export async function signOutEvents() {
	unsubscribeAll();
	
	const auth = getAuth();

	if (auth.currentUser) {
		await signOut(auth);
	}
}

/**
 * Resolves once Firebase Auth has a signed-in user on the default app.
 * Useful for callers that need to start Firestore subscriptions only after
 * signInEvents() has completed (e.g. right after login, before the auth
 * promise issued by assignUserGroup has resolved).
 */
export function waitForEventsAuth() {
	const auth = getAuth();

	if (auth.currentUser) return Promise.resolve(auth.currentUser);

	return new Promise((resolve) => {
		const unsubscribe = onAuthStateChanged(auth, (user) => {
			if (user) {
				unsubscribe();
				resolve(user);
			}
		});
	});
}

// `subscriptionKey` allows multiple concurrent listeners on the same
// collection (e.g. one per conversation state); it defaults to the collection
// name for single-listener collections.
// `handlers.waitForServer` drops the initial cache-only snapshot so the listener
// seeds from the server, not from whatever other listeners left in the cache.
export function subscribeToCol(collectionName, queryConstraints, handlers = {}, subscriptionKey = collectionName) {
	// Auto-unsubscribe previous subscription for the same key
	unsubscribeCollection(subscriptionKey);

	const colRef = collection(db, collectionName);
	const q = query(colRef, ...queryConstraints);

	let isInitialLoad = true;

	// waitForServer needs metadata events: when the server confirms data that is
	// identical to the cache, Firestore only reports it as a metadata change and
	// the listener would otherwise never fire after the dropped cache snapshot.
	const options = { includeMetadataChanges: !!handlers.waitForServer };

	const unsubscribe = onSnapshot(q, options, (snapshot) => {
		if (isInitialLoad && handlers.waitForServer && snapshot.metadata.fromCache) return;

		// Once seeded, skip metadata-only snapshots (fromCache/hasPendingWrites flips).
		if (!isInitialLoad && handlers.waitForServer && snapshot.docChanges().length === 0) return;

		if (handlers.onSnapshot) {
			isInitialLoad = false;
			handlers.onSnapshot(snapshot);
			return;
		}

		if (isInitialLoad) {
			isInitialLoad = false;
			
			if (handlers.onInitialLoad) {
				handlers.onInitialLoad(snapshot);
				return;
			}
		}

		snapshot.docChanges().forEach(change => {
			const docData = { id: change.doc.id, ...change.doc.data() };

			if (change.type === "added" && handlers.onAdded) {
				handlers.onAdded(docData, change.doc.id, snapshot);
			} else if (change.type === "modified" && handlers.onUpdated) {
				handlers.onUpdated(docData, change.doc.id, snapshot);
			} else if (change.type === "removed" && handlers.onRemoved) {
				// The snapshot lets handlers on limit()ed queries tell a real
				// deletion apart from a window eviction.
				handlers.onRemoved(docData, change.doc.id, snapshot);
			}
		});
	}, (error) => {
		if (handlers.onError) {
			handlers.onError(error);
		} else {
			console.error(`[FIRESTORE-EVENTS] Snapshot error on ${collectionName}:`, {
				message: error.message,
				code: error.code,
				name: error.name,
				details: error
			});
		}
	});

	activeSubscriptions.set(subscriptionKey, {
		unsubscribe,
		onUnsubscribed: handlers.onUnsubscribed
	});

	return unsubscribe;
}

export function subscribeToDoc(collectionName, docId, onChange, onError) {
	const docRef = doc(db, collectionName, docId);

	const unsubscribe = onSnapshot(docRef, (snapshot) => {
		if (snapshot.exists()) {
			onChange({ id: snapshot.id, ...snapshot.data() });
		}
	}, (error) => {
		if (onError) {
			onError(error);
		} else {
			console.error(`[FIRESTORE-EVENTS] Document snapshot error on ${collectionName}/${docId}:`, error);
		}
	});

	activeSubscriptions.set(`${collectionName}/${docId}`, {
		unsubscribe,
		onUnsubscribed: handlers.onUnsubscribed
	});

	return unsubscribe;
}

export function unsubscribeCollection(collectionName) {
	const sub = activeSubscriptions.get(collectionName);

	if (sub) {
		try {
			sub.unsubscribe();
			if (sub.onUnsubscribed) {
				sub.onUnsubscribed();
			}
			activeSubscriptions.delete(collectionName);
		} catch (error) {
			console.error(`[FIRESTORE-EVENTS] Failed to unsubscribe from ${collectionName}:`, error);
		}
	}
}
